import { useEffect, useState } from "react";
import { SidebarLayout } from "@/layouts/SidebarLayout";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { Mail, Share2, Trash2, Eye } from "lucide-react";
import { ViewListDialog } from "@/components/lists/ViewListDialog";
import { useSetAtom } from 'jotai';
import { campaignWizardAtom } from '@/lib/atoms';
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { captureError } from '@/lib/sentry';

interface List {
  id: string;
  name: string;
  created_at: string;
  contact_count: number;
}

const listCategories = [
  { id: "all", name: "All Lists" },
  { id: "shared", name: "Shared Lists" },
  { id: "archived", name: "Archived" },
];

export function ListsPage() {
  const [lists, setLists] = useState<List[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedList, setSelectedList] = useState<List | null>(null);
  const setCampaignWizard = useSetAtom(campaignWizardAtom);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  const fetchLists = async () => {
    if (!currentWorkspace) return;

    try {
      setLoading(true);
      // Fetch lists with contact count
      const { data: listData, error: listError } = await supabase
        .from('list_details')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });

      if (listError) throw listError;

      setLists(listData || []);
    } catch (error) {
      console.error('Error fetching lists:', error);
      captureError(error as Error, { feature: 'lists-list-fetch' });
      toast({
        title: "Error",
        description: "Failed to load lists",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEmailList = async (listId: string) => {
    try {
      const { data, error } = await supabase
        .rpc('get_contacts_by_list_id', { p_list_id: listId });

      if (error) throw error;

      setCampaignWizard({ open: true, selectedContacts: data || [] });
    } catch (error) {
      console.error(`Error fetching contacts for list ${listId}:`, error);
      captureError(error as Error, { feature: 'lists-list-email-action' });
      toast({
        title: "Error",
        description: "Could not load contacts for this list.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (currentWorkspace) {
      fetchLists();
    }
  }, [currentWorkspace?.id]);

  const deleteList = async (listId: string) => {
    try {
      const { error } = await supabase
        .from('lists')
        .delete()
        .eq('id', listId);

      if (error) throw error;

      toast({
        description: "List deleted successfully",
      });
      fetchLists();
    } catch (error) {
      captureError(error as Error, { feature: 'lists-list-delete' });
      toast({
        title: "Error",
        description: "Failed to delete list",
        variant: "destructive",
      });
    }
  };

  const Sidebar = (
    <div className="space-y-4">
      <div className="font-semibold text-lg">Lists</div>
      <ScrollArea className="h-[calc(100vh-8rem)]">
        <div className="space-y-1">
          {listCategories.map((category) => (
            <Button
              key={category.id}
              variant="ghost"
              className={cn(
                "w-full justify-start",
                category.id === activeCategory && "bg-accent"
              )}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.name}
            </Button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );

  if (!currentWorkspace) {
    return (
      <SidebarLayout sidebar={Sidebar}>
        <div className="h-full p-6">
          <div className="text-center py-8 text-muted-foreground">
            No workspace selected
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout sidebar={Sidebar}>
      <div className="h-full p-6">
        <h1 className="text-2xl font-semibold tracking-tight mb-6">All Lists</h1>
        
        {loading ? (
          <div className="text-center py-8">Loading lists...</div>
        ) : lists.length > 0 ? (
          <div className="space-y-2">
            {lists.map((list) => (
              <div
                key={list.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-6 flex-1">
                  <span className="font-medium">{list.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {list.contact_count} contacts
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(list.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setSelectedList(list)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => {
                      setSelectedList(list);
                      handleEmailList(list.id);
                    }}
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon">
                    <Share2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-destructive"
                    onClick={() => deleteList(list.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No lists yet. Select contacts and click "Add to List" to create your first list.
          </div>
        )}

        {selectedList && (
          <ViewListDialog
            open={!!selectedList}
            onOpenChange={(open) => !open && setSelectedList(null)}
            listId={selectedList.id}
            listName={selectedList.name}
          />
        )}
      </div>
      
    </SidebarLayout>
  );
}